/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
import styles from './Login.module.css';
import BrandLogo from './BrandLogo';
import loadingStyles from './shared/Loading.module.css';
import config, { logger } from '../config';
import { checkGoogleUserExists, getApiHeaders, tryUpdateUserName } from '../utils/api';
import { getFirstName, persistAuthUser, rememberSignupCollectedName } from '../utils/helper';
import { identifyUser, capture, SIGNED_IN_EVENT } from '../analytics/posthog';
import { requestGoogleSession, submitGoogleLogin, handleFacebookLogin } from '../utils/socialLogin';
import OnboardingInitial from './OnboardingInitial';
import ExtendedWelcomeFlow from './ExtendedWelcomeFlow/ExtendedWelcomeFlow';

function readPendingGoogleSignup() {
  try {
    const raw = sessionStorage.getItem(config.STORAGE_KEYS.PENDING_GOOGLE_SIGNUP);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.accessToken !== 'string' || !parsed.accessToken) return null;
    if (typeof parsed.email !== 'string' || !parsed.email) return null;
    return {
      accessToken: parsed.accessToken,
      fullName: typeof parsed.fullName === 'string' ? parsed.fullName : '',
      email: parsed.email,
      profilePicture: typeof parsed.profilePicture === 'string' ? parsed.profilePicture : '',
      nameConfirmed: parsed.nameConfirmed === true,
    };
  } catch {
    return null;
  }
}

function writePendingGoogleSignup(session) {
  try {
    sessionStorage.setItem(config.STORAGE_KEYS.PENDING_GOOGLE_SIGNUP, JSON.stringify({
      accessToken: session.accessToken,
      fullName: session.fullName || '',
      email: session.email,
      profilePicture: session.profilePicture || '',
      nameConfirmed: session.nameConfirmed === true,
    }));
  } catch {
    /* ignore private mode */
  }
}

function clearPendingGoogleSignup() {
  try {
    sessionStorage.removeItem(config.STORAGE_KEYS.PENDING_GOOGLE_SIGNUP);
  } catch {
    /* ignore private mode */
  }
}

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/home';
  const posthog = usePostHog();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [socialLoading, setSocialLoading] = useState('');
  const [googleStep, setGoogleStep] = useState(null);
  const [pendingGoogle, setPendingGoogle] = useState(null);
  const [resumingGoogle, setResumingGoogle] = useState(() => Boolean(readPendingGoogleSignup()));

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // A refresh mid-welcome keeps the Google session, but only if the account is still new.
  useEffect(() => {
    const pending = readPendingGoogleSignup();
    if (!pending) return undefined;
    let cancelled = false;

    (async () => {
      const existsResult = await checkGoogleUserExists(pending.accessToken);
      if (cancelled) return;
      if (!existsResult.ok || existsResult.exists) {
        clearPendingGoogleSignup();
        setPendingGoogle(null);
        setGoogleStep(null);
        setResumingGoogle(false);
        return;
      }
      setPendingGoogle(pending);
      setGoogleStep(pending.nameConfirmed ? 'welcome' : 'name');
      setResumingGoogle(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError('');
      setErrorCode('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setErrorCode('');

    try {
      const response = await fetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.LOGIN}`, {
        method: 'POST',
        headers: getApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        persistAuthUser({
          token: data.data.token,
          name: data.data.fullName,
          email: data.data.email || formData.email,
        });

        identifyUser(posthog, data.data.token, { name: data.data.fullName, email: formData.email });
        capture(posthog, SIGNED_IN_EVENT, { method: 'email' });
        navigate(redirectPath);
      } else {
        // Handle error responses from API
        let errorMessage = data.message || 'Login failed. Please try again.';

        // Handle validation errors if they exist
        if (data.validation_errors && Array.isArray(data.validation_errors)) {
          errorMessage = data.validation_errors.join('\n');
        }

        setError(errorMessage);
        if (data.error_code) {
          setErrorCode(data.error_code);
        }
      }
    } catch (err) {
      logger.error('Login error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLoginClick = async (provider) => {
    setSocialLoading(provider);
    setError('');
    setErrorCode('');

    try {
      let result;

      switch (provider) {
        case 'Google': {
          const session = await requestGoogleSession();
          const existsResult = await checkGoogleUserExists(session.accessToken);
          if (!existsResult.ok) {
            throw new Error(existsResult.message || 'Could not check this account.');
          }
          if (!existsResult.exists) {
            const pending = { ...session, nameConfirmed: false };
            writePendingGoogleSignup(pending);
            setPendingGoogle(pending);
            setGoogleStep('name');
            return;
          }
          result = await submitGoogleLogin(session);
          break;
        }
        case 'Facebook':
          result = await handleFacebookLogin();
          break;
        default:
          throw new Error(`Unsupported provider: ${provider}`);
      }

      if (result.success) {
        // Store authentication data
        if (result.data && result.data.token) {
          persistAuthUser({
            token: result.data.token,
            name: result.data.fullName,
            email: result.data.email,
          });
          identifyUser(posthog, result.data.token, {
            name: result.data.fullName,
            email: result.data.email,
          });
          capture(posthog, SIGNED_IN_EVENT, { method: provider.toLowerCase() });
        }
        navigate(redirectPath);
      } else {
        throw new Error(result.message || `${provider} login failed`);
      }
    } catch (err) {
      logger.error(`${provider} login error:`, err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (err?.errorCode === 'ACCOUNT_DEACTIVATED') {
        setError(errorMessage);
        setErrorCode('ACCOUNT_DEACTIVATED');
      } else {
        setError(`${provider} login failed: ${errorMessage}`);
      }
    } finally {
      setSocialLoading('');
    }
  };

  const finishGoogleSession = async (session, blocker) => {
    const confirmedName = (session.fullName || '').trim();
    const result = await submitGoogleLogin({
      ...session,
      fullName: confirmedName,
      extendedWelcomeBlocker: blocker,
    });
    clearPendingGoogleSignup();
    if (result.data && result.data.token) {
      const accountName = confirmedName || result.data.fullName || '';
      if (confirmedName && confirmedName !== (result.data.fullName || '').trim()) {
        await tryUpdateUserName(result.data.token, confirmedName);
      }
      persistAuthUser({
        token: result.data.token,
        name: accountName,
        email: result.data.email,
      });
      if (accountName) rememberSignupCollectedName(accountName);
      identifyUser(posthog, result.data.token, {
        name: accountName,
        email: result.data.email,
      });
      capture(posthog, SIGNED_IN_EVENT, { method: 'google' });
    }
    navigate('/v2-onboarding-agent');
  };

  const handleGoogleNameContinue = (name) => {
    const next = { ...pendingGoogle, fullName: name, nameConfirmed: true };
    writePendingGoogleSignup(next);
    setPendingGoogle(next);
    setGoogleStep('welcome');
  };

  const handleGoogleNameBack = () => {
    clearPendingGoogleSignup();
    setPendingGoogle(null);
    setGoogleStep(null);
  };

  const handleWelcomeCommitted = async ({ blocker }) => {
    if (!pendingGoogle) {
      throw new Error('Google sign-in expired. Please try again.');
    }
    await finishGoogleSession(pendingGoogle, blocker);
  };

  const handleWelcomeExit = () => {
    if (!pendingGoogle) {
      setGoogleStep(null);
      return;
    }
    const next = { ...pendingGoogle, nameConfirmed: false };
    writePendingGoogleSignup(next);
    setPendingGoogle(next);
    setGoogleStep('name');
  };

  const handleSignUpClick = (e) => {
    e.preventDefault();
    navigate('/signup');
  };

  const handleForgotPassword = () => {
    // Navigate to forgot password page with email if available
    const email = formData.email ? `?email=${encodeURIComponent(formData.email)}` : '';
    navigate(`/forgot-password${email}`);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (resumingGoogle) {
    return <main className={styles['login-container']} aria-busy="true" aria-label="Checking Google sign-in" />;
  }

  if (googleStep === 'name' && pendingGoogle) {
    return (
      <OnboardingInitial
        initialName={pendingGoogle.fullName}
        onContinue={handleGoogleNameContinue}
        onBack={handleGoogleNameBack}
      />
    );
  }

  if (googleStep === 'welcome' && pendingGoogle) {
    return (
      <ExtendedWelcomeFlow
        entry="login"
        firstName={getFirstName(pendingGoogle.fullName)}
        onCommitted={handleWelcomeCommitted}
        onExit={handleWelcomeExit}
      />
    );
  }

  return (
    <main className={styles['login-container']} id="login-main" aria-label="Log in">
      <div className={styles['login-content']}>
        <div className={styles['logo-wrap']}>
          <BrandLogo alt="Richuel Logo" />
        </div>

        <div className={styles['login-form-container']}>
          <h1 className={styles['login-title']}>Welcome</h1>

          <div className={styles['signup-link']}>
            <p>Don't have an account? <a href="/signup" onClick={handleSignUpClick}>Sign up here</a></p>
          </div>

          <div className={styles['social-login']} role="group" aria-label="Sign in with social account">
            <button
              type="button"
              className={`${styles['social-button']} ${styles['google']}`}
              onClick={() => handleSocialLoginClick('Google')}
              disabled={socialLoading === 'Google'}
              aria-label={socialLoading === 'Google' ? 'Signing in with Google…' : 'Sign in with Google'}
            >
              {socialLoading === 'Google' ? (
                <div className={loadingStyles['loading-spinner']}>
                  <div className={loadingStyles['spinner-ring']}></div>
                  <div className={loadingStyles['spinner-ring']}></div>
                  <div className={loadingStyles['spinner-ring']}></div>
                </div>
              ) : (
                <svg className={styles['google-icon']} viewBox="0 0 24 24" width="24" height="24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              className={`${styles['social-button']} ${styles['facebook']}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              disabled
              aria-label="Facebook login (currently unavailable)"
            >
              <svg className={styles['facebook-icon']} viewBox="0 0 24 24" width="24" height="24">
                <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </button>
          </div>

          {(error || errorCode === 'ACCOUNT_DEACTIVATED') && (
            <div
              key={error || 'account-deactivated'}
              id="login-error"
              className={styles['error-message']}
              role="alert"
              aria-live="polite"
            >
              {errorCode === 'ACCOUNT_DEACTIVATED' ? (
                <div>
                  Your account has been deactivated. To reactivate your account, please email{' '}
                  <a href="mailto:team@richuel.com">team@richuel.com</a>.
                </div>
              ) : (
                error.split('\n').map((errorLine, index) => (
                  <div key={index}>{errorLine}</div>
                ))
              )}
            </div>
          )}

          <form className={styles['login-form']} onSubmit={handleSubmit} aria-label="Log in with email">
            <div className={styles['form-group']}>
              <label htmlFor="login-email">Email</label>
              <input
                type="email"
                id="login-email"
                name="email"
                placeholder="Enter here"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                autoComplete="email"
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            <div className={styles['form-group']}>
              <label htmlFor="login-password">Password</label>
              <div className={styles['password-input-container']}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="login-password"
                  name="password"
                  placeholder="Enter here"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                  aria-describedby={error ? 'login-error' : undefined}
                />
                <button
                  type="button"
                  className={styles['password-toggle']}
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
                    {showPassword ? (
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    ) : (
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={styles['login-button']}
              disabled={isLoading}
              aria-busy={isLoading}
              aria-label={isLoading ? 'Logging in…' : 'Continue'}
            >
              {isLoading ? 'Logging in...' : 'Continue'}
            </button>
          </form>

          <div className={styles['forgot-password']}>
            <button type="button" onClick={handleForgotPassword} aria-label="Go to forgot password page">
              Forgot password?
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login; 